import React, { useState } from "react";
import { Button, Card, CardBody } from "reactstrap";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../util/api";
import { DetailedErrorAlert } from "../components/DetailedErrorAlert";
import { LoadingIcon } from "../components/LoadingIcon";
import { CoverageSearchForm } from "./CoverageSearchForm";
import { CoverageResultsTable } from "./CoverageResultsTable";
import { ExportButton } from "./ExportButton";

export const Coverages = () => {
  const [payorIds, setPayorIds] = useState([]);
  const [procedureCodes, setProcedureCodes] = useState([]);
  const [submitted, setSubmitted] = useState(null);
  const [results, setResults] = useState([]);
  const [meta, setMeta] = useState(null);
  // Cursors used to fetch each visited page.
  const [cursors, setCursors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchPage = async (filters, nextCursors) => {
    setLoading(true);
    setError(null);

    try {
      const { data } = await api.get("/coverages.json", {
        params: {
          payor_ids: filters.payorIds,
          procedure_codes: filters.procedureCodes,
          cursor: nextCursors[nextCursors.length - 1],
        },
      });

      setResults(data.resources);
      setMeta(data.meta);
      setCursors(nextCursors);
      // Keep export filters aligned with the displayed results.
      setSubmitted(filters);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => fetchPage({ payorIds, procedureCodes }, [null]);
  const handleNext = () => fetchPage(submitted, [...cursors, meta.next_cursor]);
  const handlePrev = () => fetchPage(submitted, cursors.slice(0, -1));

  const pageIndex = cursors.length - 1;
  const firstRow = pageIndex * (meta?.per ?? 0) + 1;
  const lastRow = firstRow + results.length - 1;

  return (
    <div className="p-4">
      <CoverageSearchForm
        payorIds={payorIds}
        setPayorIds={setPayorIds}
        procedureCodes={procedureCodes}
        setProcedureCodes={setProcedureCodes}
        onSearch={handleSearch}
        loading={loading}
      />

      <DetailedErrorAlert error={error} />

      {loading && (
        <Card className="mb-4" aria-busy="true">
          <CardBody className="d-flex align-items-center justify-content-center py-5" role="status">
            <LoadingIcon className="me-2 text-primary" />
            <span>Loading coverages...</span>
          </CardBody>
        </Card>
      )}

      {meta && !loading && (
        <Card>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <span className="text-muted">
                {results.length ? `Showing ${firstRow}–${lastRow} of ${meta.total_count} coverages` : "No coverages matched your search"}
              </span>
              <ExportButton
                payorIds={submitted?.payorIds}
                procedureCodes={submitted?.procedureCodes}
                disabled={!results.length}
                onError={setError}
              />
            </div>

            {results.length > 0 && <CoverageResultsTable results={results} />}

            {(results.length > 0 || pageIndex > 0) && (
              <div className="d-flex justify-content-between align-items-center">
                <Button color="secondary" outline size="sm" disabled={pageIndex === 0} onClick={handlePrev}>
                  <ChevronLeft size={16} /> Prev
                </Button>
                <Button color="secondary" outline size="sm" disabled={!meta.has_more} onClick={handleNext}>
                  Next <ChevronRight size={16} />
                </Button>
              </div>
            )}
          </CardBody>
        </Card>
      )}
    </div>
  );
};
